import React from "react"
import { graphql } from "gatsby"
import {
  Link,
  Trans,
  useI18next,
  useTranslation,
} from "@zestia/gatsby-plugin-react-i18next"
import findImage from "@/utils/find-image"

import SharedHead from "@/components/SharedHead"
import MainLayout from "@/layouts/MainLayout"

import IconChevronRight from "@/components/svg/icons/IconChevronRight"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import FullWidthCTABanner from "@/components/FullWidthCTABanner"
import transformBlogPosts from "@/utils/transformBlogPosts"
import SanityImage from "gatsby-plugin-sanity-image"
import truncateString from "@/utils/truncateString"
import FeaturesCarousel from "@/components/FeaturesCarousel"
import QuotesCarousel from "@/components/QuotesCarousel"
import StackingContentBlockContainer from "@/components/StackingContentBlockContainer"

// Integration icons
import CyclrIcon from "@/images/integrations/flat-icons/cyclr.svg"
import KashflowIcon from "@/images/integrations/flat-icons/kashflow.svg"
import QuotientIcon from "@/images/integrations/flat-icons/quotient.svg"
import FormstackIcon from "@/images/integrations/flat-icons/formstack.svg"
import HelpScoutIcon from "@/images/integrations/flat-icons/helpscout.svg"
import JustCallIcon from "@/images/integrations/flat-icons/justcall.svg"
import CallBellIcon from "@/images/integrations/flat-icons/callbell.svg"
import MailchimpIcon from "@/images/integrations/flat-icons/mailchimp.svg"
import MakeIcon from "@/images/integrations/flat-icons/make.svg"
import OsanoIcon from "@/images/integrations/flat-icons/osano.svg"
import FreshBooksIcon from "@/images/integrations/flat-icons/freshbooks.svg"
import CircleLoopIcon from "@/images/integrations/flat-icons/circleloop.svg"
import TogglIcon from "@/images/integrations/flat-icons/toggl.svg"
import betterproposalsIcon from "@/images/integrations/flat-icons/betterproposals.svg"
import ZendeskIcon from "@/images/integrations/flat-icons/zendesk.svg"
import G2StarsOrange from "@/components/svg/G2StarsOrange"
import FAQ from "@/components/FAQ"
import G2Badges from "@/components/G2Badges"
// import FeatureCard from "@/components/FeatureCard"

export const Head = props => {
  return (
    <>
      <SharedHead {...props} />
      <body className="page-index bg-grey" />
    </>
  )
}

const IndexPage = ({ data }) => {
  const { t } = useTranslation()

  // Current language
  const { language } = useI18next()

  // Feature blocks data
  const features = [
    // Contact Management
    {
      kicker: t("home.features.contact-management.kicker"),
      title: t("home.features.contact-management.title"),
      description: t("home.features.contact-management.text"),
      image: {
        src: getImage(findImage(data.allFile, "feature-contact-management")),
        alt: t("home.features.contact-management.title"),
      },
      link: { url: "/features/contact-management-software" },
    },
    // Tasks
    {
      kicker: t("home.features.tasks-and-calendar.kicker"),
      title: t("home.features.tasks-and-calendar.title"),
      description: t("home.features.tasks-and-calendar.text"),
      image: {
        src: getImage(findImage(data.allFile, "feature-tasks")),
        alt: t("home.features.tasks-and-calendar.title"),
      },
      link: { url: "/features/tasks-and-calendar" },
    },
    // Sales Pipeline
    {
      kicker: t("home.features.sales-pipeline.kicker"),
      title: t("home.features.sales-pipeline.title"),
      description: t("home.features.sales-pipeline.text"),
      image: {
        src: getImage(findImage(data.allFile, "feature-sales-pipeline")),
        alt: t("home.features.sales-pipeline.title"),
      },
      link: { url: "/features/sales-pipeline" },
    },
    // Project Management
    {
      kicker: t("home.features.project-management.kicker"),
      title: t("home.features.project-management.title"),
      description: t("home.features.project-management.text"),
      image: {
        src: getImage(findImage(data.allFile, "feature-project-management")),
        alt: t("home.features.project-management.title"),
      },
      link: { url: "/features/project-management-crm" },
    },
    // Sales Analytics
    {
      kicker: t("home.features.sales-analytics.kicker"),
      title: t("home.features.sales-analytics.title"),
      description: t("home.features.sales-analytics.text"),
      image: {
        src: getImage(findImage(data.allFile, "feature-sales-analytics")),
        alt: t("home.features.sales-analytics.title"),
      },
      link: { url: "/features/sales-analytics" },
    },
  ]

  // Quotes
  const quotes = [
    {
      kicker: t("common.testimonials.1.kicker"),
      quote: t("common.testimonials.1.quote"),
      name: t("common.testimonials.1.name"),
      role: t("common.testimonials.1.role"),
    },
    {
      kicker: t("common.testimonials.2.kicker"),
      quote: t("common.testimonials.2.quote"),
      name: t("common.testimonials.2.name"),
      role: t("common.testimonials.2.role"),
    },
    {
      kicker: t("common.testimonials.3.kicker"),
      quote: t("common.testimonials.3.quote"),
      name: t("common.testimonials.3.name"),
      role: t("common.testimonials.3.role"),
    },
  ]

  // Combine and transform blog posts from both sources
  const allBlogPosts = transformBlogPosts({
    allMdx: data.allMdx,
    allSanityBlogPost: data.allSanityBlogPost,
  })

  const findBlogPost = slug => {
    return allBlogPosts.find(post => post.frontmatter.slug.includes(slug))
  }

  // Blogs to show for each language
  const featuredBlogs = {
    en: [
      findBlogPost("/blog/10-best-free-ai-tools-for-productive-businesses"),
      findBlogPost("/blog/how-to-implement-a-crm"),
      findBlogPost(
        "/blog/make-the-most-of-zapier-and-capsule-use-cases-across-the-business",
      ),
    ],
    es: [
      findBlogPost("/blog/best-telephone-voip-crm-integrations"),
      findBlogPost("/blog/webinar-kpis-for-sellers"),
      findBlogPost("/blog/endless-integration-opportunities-zapier"),
    ],
    fr: [
      findBlogPost("/blog/best-xero-crm-integrations"),
      findBlogPost("/blog/quickbooks-crm-integration-reviews"),
      findBlogPost("/blog/capsule-outlook-addin-latest-enhancements"),
    ],
    pt: [
      findBlogPost("/blog/quickbooks-crm-integration-reviews"),
      findBlogPost("/blog/best-xero-crm-integrations"),
      findBlogPost("/blog/moving-crms"),
    ],
  }

  // Small integration icons
  const smallIntegrationIcons = [
    {
      icon: FreshBooksIcon,
      title: "FreshBooks",
    },
    {
      icon: HelpScoutIcon,
      title: "Help Scout",
    },
    {
      icon: TogglIcon,
      title: "Toggl",
    },
    {
      icon: FormstackIcon,
      title: "Formstack",
    },
    {
      icon: MailchimpIcon,
      title: "Mailchimp",
    },
    {
      icon: CircleLoopIcon,
      title: "CircleLoop",
    },
    {
      icon: MakeIcon,
      title: "Make",
    },
    {
      icon: CyclrIcon,
      title: "Cyclr",
    },
    {
      icon: KashflowIcon,
      title: "Kashflow",
    },
    {
      icon: JustCallIcon,
      title: "JustCall",
    },
    {
      icon: OsanoIcon,
      title: "Osano",
    },
    {
      icon: CallBellIcon,
      title: "CallBell",
    },
    {
      icon: ZendeskIcon,
      title: "Zendesk",
    },
    {
      icon: betterproposalsIcon,
      title: "Better Proposals",
    },
    {
      icon: QuotientIcon,
      title: "Quotient",
    },
  ]

  return (
    <MainLayout
      trackingData={{
        content_category: "homepage",
      }}
      className="homepage"
    >
      {/* Header */}
      <div className="container-fluid text-centered pt-16 pb-16">
        <h1 className="h1--xl text-balance SharpGrotesk">
          {t("home.header.win-more")}
          <br />
          {t("home.header.win-more-sub")}
        </h1>

        <div className="row center-xs">
          <p className="p--lg col-md-5">{t("home.header.delight-customers")}</p>
        </div>

        <Link to="/signup" className="btn rounded btn--lg btn--dark mt-6">
          {t("common.try-free")}
        </Link>
      </div>

      {/* Features carousel */}
      <FeaturesCarousel />

      {/* G2 Badges */}
      <div className="container-fluid row center-xs mt-24 mb-4">
        <h2 className="h5 col-xs-12">{t("sales-resources.trusted")}</h2>

        <G2Badges className="col-md-6" />
      </div>

      {/* Feature blocks */}
      <StackingContentBlockContainer
        blocks={features}
        title={t("home.features.heading")}
        subheading={t("home.features.subheading")}
      />

      {/* Testimonial slider */}
      <div className="bg-white pt-8 pb-8">
        <div className="container-fluid mt-16 md:mt-30 mb-16 md:mb-30">
          <QuotesCarousel quotes={quotes} className="homepage__testimonials">
            <div className="homepage__testimonials__g2-rating">
              <G2StarsOrange label={t("sales-resources.reviews.one")} />

              <div>
                <span className="mb-0 p--sm">
                  {t("sales-resources.g2.rated")}
                </span>

                <span className="mb-0 p--sm homepage__testimonials__review-count">
                  {t("sales-resources.g2.reviews")}
                </span>
              </div>
            </div>

            <h2 className="mt-2 h3">
              <Trans
                i18nKey={"home.customer-ratings.title"}
                components={{
                  br: <br />,
                }}
              />
            </h2>

            <div className="homepage__testimonials__bottom">
              <a
                href="https://www.g2.com/products/capsule-crm/reviews"
                target="_blank"
                className="btn btn--large btn--tertiary homepage__testimonials__link"
              >
                {t("sales-resources.g2.view-all")}

                <svg
                  className="ml-2"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.22 6.72C4.08752 6.86217 4.0154 7.05022 4.01882 7.24452C4.02225 7.43882 4.10096 7.62421 4.23838 7.76162C4.37579 7.89903 4.56118 7.97775 4.75548 7.98118C4.94978 7.9846 5.13782 7.91248 5.28 7.78L10.5 2.56V4.25C10.5 4.44891 10.579 4.63968 10.7197 4.78033C10.8603 4.92098 11.0511 5 11.25 5C11.4489 5 11.6397 4.92098 11.7803 4.78033C11.921 4.63968 12 4.44891 12 4.25V0.75C12 0.551088 11.921 0.360322 11.7803 0.21967C11.6397 0.0790175 11.4489 0 11.25 0H7.75C7.55109 0 7.36032 0.0790175 7.21967 0.21967C7.07902 0.360322 7 0.551088 7 0.75C7 0.948912 7.07902 1.13968 7.21967 1.28033C7.36032 1.42098 7.55109 1.5 7.75 1.5H9.44L4.22 6.72Z"
                    fill="#0F172A"
                  />
                  <path
                    d="M1.5 4.75C1.5 4.06 2.06 3.5 2.75 3.5H5C5.19891 3.5 5.38968 3.42098 5.53033 3.28033C5.67098 3.13968 5.75 2.94891 5.75 2.75C5.75 2.55109 5.67098 2.36032 5.53033 2.21967C5.38968 2.07902 5.19891 2 5 2H2.75C2.02065 2 1.32118 2.28973 0.805456 2.80546C0.289731 3.32118 0 4.02065 0 4.75V9.25C0 9.97935 0.289731 10.6788 0.805456 11.1945C1.32118 11.7103 2.02065 12 2.75 12H7.25C7.97935 12 8.67882 11.7103 9.19454 11.1945C9.71027 10.6788 10 9.97935 10 9.25V7C10 6.80109 9.92098 6.61032 9.78033 6.46967C9.63968 6.32902 9.44891 6.25 9.25 6.25C9.05109 6.25 8.86032 6.32902 8.71967 6.46967C8.57902 6.61032 8.5 6.80109 8.5 7V9.25C8.5 9.94 7.94 10.5 7.25 10.5H2.75C2.06 10.5 1.5 9.94 1.5 9.25V4.75Z"
                    fill="#0F172A"
                  />
                </svg>
              </a>
            </div>
          </QuotesCarousel>
        </div>
      </div>

      {/* Integrations */}
      <div className="homepage__integrations pt-16 pb-16 md:pt-30 md:pb-30">
        <div className="container-fluid row center-xs">
          <h2 className="col-xs-12">{t("home.integrations.title")}</h2>

          <p className="col-md-6 text-balance p--lg">
            {t("home.integrations.description")}
          </p>

          <div className="homepage__integrations__grid mt-10 col-xs-12">
            {/* Quickbooks */}
            <Link
              to="/integrations/quickbooks-crm/"
              className="homepage__integrations__integration"
            >
              <div
                className="homepage__integrations__integration__logo"
                style={{
                  background: "white",
                }}
              >
                <img
                  src="/icons/integrations/quickbooks.svg"
                  alt=""
                  width={30}
                  height={30}
                />
              </div>
              QuickBooks
            </Link>

            {/* Zapier */}
            <Link
              to="/integrations/zapier-crm/"
              className="homepage__integrations__integration"
            >
              <div
                className="homepage__integrations__integration__logo"
                style={{
                  background: "#FF4F00",
                }}
              >
                <img
                  src="/icons/integrations/zapier.svg"
                  alt=""
                  width={30}
                  height={30}
                />
              </div>
              Zapier
            </Link>

            {/* Slack */}
            <Link
              to="/integrations/slack/"
              language="en"
              className="homepage__integrations__integration"
            >
              <div
                className="homepage__integrations__integration__logo"
                style={{
                  background: "white",
                }}
              >
                <img
                  src="/icons/integrations/slack.svg"
                  alt=""
                  width={30}
                  height={30}
                />
              </div>
              Slack
            </Link>

            {/* WhatsApp */}
            <Link
              to="/integrations/?collection=whatsapp"
              className="homepage__integrations__integration"
            >
              <div
                className="homepage__integrations__integration__logo"
                style={{
                  background: "#25D366",
                }}
              >
                <img
                  src="/icons/integrations/whatsapp.svg"
                  alt=""
                  width={30}
                  height={30}
                />
              </div>
              WhatsApp
            </Link>

            {/* Google */}
            <Link
              to="/integrations/?collection=google"
              className="homepage__integrations__integration"
            >
              <div
                className="homepage__integrations__integration__logo"
                style={{
                  background: "white",
                }}
              >
                <img
                  src="/icons/integrations/google.svg"
                  alt=""
                  width={30}
                  height={30}
                />
              </div>
              Google
            </Link>

            {/* Transpond */}
            <Link
              to="/integrations/transpond/"
              className="homepage__integrations__integration"
            >
              <div
                className="homepage__integrations__integration__logo"
                style={{
                  background: "#BAA5F5",
                }}
              >
                <img
                  src="/icons/integrations/transpond.svg"
                  alt=""
                  width={30}
                  height={30}
                />
              </div>
              Transpond
            </Link>

            {/* Microsoft */}
            <Link
              to="/integrations/?collection=microsoft"
              className="homepage__integrations__integration"
            >
              <div
                className="homepage__integrations__integration__logo"
                style={{
                  background: "white",
                }}
              >
                <img
                  src="/icons/integrations/microsoft.svg"
                  alt=""
                  width={30}
                  height={30}
                />
              </div>
              Microsoft
            </Link>

            {/* Sage */}
            <Link
              to="/integrations/sage-crm/"
              className="homepage__integrations__integration"
            >
              <div
                className="homepage__integrations__integration__logo"
                style={{
                  background: "#0C0B0B",
                }}
              >
                <img
                  src="/icons/integrations/sage.svg"
                  alt=""
                  width={30}
                  height={30}
                />
              </div>
              Sage
            </Link>

            {/* FreeAgent */}
            <Link
              to="/integrations/freeagent-crm/"
              className="homepage__integrations__integration"
            >
              <div
                className="homepage__integrations__integration__logo"
                style={{
                  background: "white",
                }}
              >
                <img
                  src="/icons/integrations/freeagent.svg"
                  alt=""
                  width={30}
                  height={30}
                />
              </div>
              FreeAgent
            </Link>

            {/* Xero */}
            <Link
              to="/integrations/xero-crm/"
              className="homepage__integrations__integration"
            >
              <div
                className="homepage__integrations__integration__logo"
                style={{
                  background: "#1FC0E7",
                }}
              >
                <img
                  src="/icons/integrations/xero.svg"
                  alt=""
                  width={30}
                  height={30}
                />
              </div>
              Xero
            </Link>
          </div>

          {/* Smaller integration icons */}
          <div className="homepage__integrations__small-icons mt-10 col-xs-12">
            {smallIntegrationIcons.map((icon, index) => (
              <div
                className="homepage__integrations__small-icons__icon"
                key={index}
                aria-label={icon.title}
                role="img"
              >
                {/* Tooltip */}
                <div className="homepage__integrations__small-icons__tooltip">
                  {icon.title}
                </div>

                <icon.icon />
              </div>
            ))}
          </div>

          <Link to="/integrations" className="btn btn--dark btn--lg mt-10">
            {t("common.integration-highlight-browse")}
          </Link>
        </div>
      </div>

      {/* Why Capsule */}
      <div className="bg-white">
        <div className="container-fluid pt-16 md:pt-30 pb-16 md:pb-30">
          <h2 className="h3">{t("home.why-capsule.heading")}</h2>

          <div className="row mt-10">
            <div className="col-md-6 md:pr-4 mb-4 md:mb-0">
              <h3
                className="h4"
                style={{
                  fontWeight: "500",
                }}
              >
                {t("home.why-capsule.zero-onboarding-fees.title")}
              </h3>

              <p className="p--lg">
                {t("home.why-capsule.zero-onboarding-fees.text")}
              </p>

              <h3
                className="md:mt-10 h4"
                style={{
                  fontWeight: "500",
                }}
              >
                {t("home.why-capsule.workflows-just-work.title")}
              </h3>

              <p className="p--lg">
                {t("home.why-capsule.workflows-just-work.text")}
              </p>
            </div>

            <div className="col-md-6 md:pl-4">
              <h3
                className="h4"
                style={{
                  fontWeight: "500",
                }}
              >
                {t("home.why-capsule.rapid-implementation.title")}
              </h3>

              <p className="p--lg">
                {t("home.why-capsule.rapid-implementation.text")}
              </p>

              <h3
                className="md:mt-10 h4"
                style={{
                  fontWeight: "500",
                }}
              >
                {t("home.why-capsule.your-capsule.title")}
              </h3>

              <p className="p--lg">{t("home.why-capsule.your-capsule.text")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Blog */}
      <div className="homepage__blog-highlight container-fluid row center-xs mt-16 mb-16 md:mt-30 md:mb-30">
        <h2 className="col-xs-12">{t("home.blog-highlight.title")}</h2>

        <p className="col-md-5 p--lg">{t("home.blog-highlight.description")}</p>

        <div className="homepage__blog-highlight__grid col-xs-12 mt-10">
          {/* Show blogs based on users' language */}
          {featuredBlogs[language].map((blog, index) => (
            <Link
              to={blog.frontmatter.slug}
              className="homepage__blog-highlight__blog"
              key={index}
            >
              {blog.internal.type === "SanityBlogPost" ? (
                <SanityImage
                  {...blog.frontmatter.preview_image}
                  width={200}
                  height={150}
                  alt={blog.frontmatter.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "12px",
                    objectFit: "cover",
                  }}
                  className="homepage__blog-highlight__blog__image"
                />
              ) : (
                <GatsbyImage
                  image={getImage(blog.frontmatter.preview_image.src)}
                  width={200}
                  height={150}
                  alt={blog.frontmatter.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "12px",
                    objectFit: "cover",
                  }}
                  className="homepage__blog-highlight__blog__image"
                />
              )}

              <div className="homepage__blog-highlight__blog__content pl-5 pr-5 pt-3 pb-3">
                <span className="p--sm text-bold">
                  {t(`blog.category.${blog.frontmatter.categories[0]}`)}
                </span>

                <p className="mb-0">
                  {/* Truncate if too many words */}
                  {truncateString(blog.frontmatter.title, 40)}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <Link to="/blog/" className="btn btn--dark btn--lg mt-10">
          {t("home.blog-highlight.cta")}
        </Link>
      </div>

      <div className="bg-white pt-8 pb-8">
        <FAQ
          className="md:mt-30"
          data={[
            {
              title: t("home.faqs.one.question"),
              description: t("home.faqs.one.answer"),
            },
            {
              title: t("home.faqs.two.question"),
              description: t("home.faqs.two.answer"),
            },
            {
              title: t("home.faqs.three.question"),
              description: t("home.faqs.three.answer"),
            },
            {
              title: t("home.faqs.four.question"),
              description: t("home.faqs.four.answer"),
            },
            {
              title: t("home.faqs.five.question"),
              description: t("home.faqs.five.answer"),
            },
            {
              title: t("home.faqs.six.question"),
              description: t("home.faqs.six.answer"),
            },
          ]}
        />
      </div>

      {/* Footer */}
      <FullWidthCTABanner
        text={t("common.try-capsule-free-14-days-pay-later")}
        buttonText={t("common.try-free")}
      />
    </MainLayout>
  )
}

export default IndexPage

export const query = graphql`
  query ($language: String) {
    ...Locales
    ...Integrations
    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "homepage" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData(quality: 100, placeholder: BLURRED)
          }
        }
      }
    }

    allSanitySettings {
      nodes {
        g2Badges {
          ...ImageWithPreview
        }
      }
    }

    # Get all Mdx blog posts
    allMdx(
      filter: {
        fields: {
          path: { regex: "/(mdxPages/blog)/" }
          lang: { eq: $language }
        }
        frontmatter: { hidden: { ne: true } }
      }
    ) {
      nodes {
        frontmatter {
          title
          categories
          slug
          preview_image {
            alt
            src {
              childImageSharp {
                gatsbyImageData(width: 400, placeholder: BLURRED)
              }
            }
          }
        }
        internal {
          type
        }
      }
    }

    # Get all Sanity blog posts
    allSanityBlogPost(
      filter: {
        hidden: { ne: true }
        hiddenFromPageOnly: { ne: true }
        language: { eq: $language }
      }
    ) {
      nodes {
        slug {
          current
        }
        coverImage {
          ...ImageWithPreview
        }
        title
        categories
        internal {
          type
        }
      }
    }
  }
`
